/**
 * When the person starts and ends each working day (heyname, 9 Oct 2026).
 * Founder: *"we keep a record what time he starts every day and what time he
 * ends every day, so that we can later use this data ... like tell him this
 * isn't your usual time, hope everything is great."*
 *
 * Kept on the device first (the shell marks it, no call), and sent to the
 * person's own `work_days` rows once a day from the + sheet, so it outlives a
 * cleared browser and follows them to another device. Minutes since local
 * midnight; a day is the local date.
 */

/** The local date, YYYY-MM-DD (the same as stats.ts's, kept here so the shell imports nothing else). */
const dayOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const KEY = 'karr:days';
/** Activity this close together is one stretch of work: a mark is written at most this often. */
const STEP_MS = 60_000;

export interface WorkDay {
    /** YYYY-MM-DD, local. */
    d: string;
    /** Minutes since local midnight of the first activity. */
    first: number;
    /** Minutes since local midnight of the last activity. */
    last: number;
    /** Sent to the server with these values. */
    sent?: boolean;
}

function read(): WorkDay[] {
    try {
        const raw = globalThis.localStorage?.getItem(KEY);
        const v = raw ? (JSON.parse(raw) as unknown) : [];
        return Array.isArray(v) ? (v as WorkDay[]) : [];
    } catch {
        return [];
    }
}

function write(days: WorkDay[]): void {
    try { globalThis.localStorage?.setItem(KEY, JSON.stringify(days.slice(-90))); } catch { /* a full or blocked store loses only the device copy */ }
}

const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();

let lastMark = 0;

/** The person is here now: today's first time if it has none, and its last time. */
export function markActive(now: Date = new Date()): WorkDay {
    const days = read();
    const d = dayOf(now);
    const m = minutesOf(now);
    let today = days.find((x) => x.d === d);
    if (!today) {
        today = { d, first: m, last: m };
        days.push(today);
    } else if (m > today.last) {
        today.last = m;
        today.sent = false;
    }
    write(days);
    lastMark = now.getTime();
    return today;
}

/** For the shell: mark on activity, at most once a minute, never more work than a timestamp compare. */
export function markActiveSoon(): void {
    const t = Date.now();
    if (t - lastMark >= STEP_MS) markActive(new Date(t));
}

export function workDays(): WorkDay[] {
    return read();
}

function median(xs: number[]): number | null {
    if (xs.length < 5) return null;
    const s = [...xs].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
}

/** The usual first and last minute over the 14 days before today, once there are five of them. */
export function usualTimes(days: readonly WorkDay[], today: string): { start: number | null; end: number | null } {
    const before = days.filter((x) => x.d < today).slice(-14);
    return { start: median(before.map((x) => x.first)), end: median(before.map((x) => x.last)) };
}

/** "9 am", "12:30 pm": how a person says a time. */
export function clockWords(minutes: number): string {
    const r = (Math.round(minutes / 30) * 30) % 1440;
    const h = Math.floor(r / 60);
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}${r % 60 ? ':30' : ''} ${h < 12 ? 'am' : 'pm'}`;
}

/** Days not yet sent, finished ones and today's so far. */
export function unsent(): WorkDay[] {
    return read().filter((x) => !x.sent);
}

export function markSent(sent: readonly WorkDay[]): void {
    const days = read();
    for (const s of sent) {
        const x = days.find((y) => y.d === s.d);
        if (x && x.first === s.first && x.last === s.last) x.sent = true;
    }
    write(days);
}
